import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { Button } from "../ui/button.jsx";
import { ArrowDown } from "lucide-react";

const Hero = () => {
    const handleScroll = () => {
        const focusSection = document.getElementById('our-focus');
        if (focusSection) {
            focusSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        // Changed bg to light (slate-50) but removed the image as requested
        <section className="relative h-[90vh] flex justify-center items-center overflow-hidden bg-background">

            {/* z-0: Floating Geometric Shapes */}
            {/* RESTORED: usage of 'bg-brand' and 'bg-aiApplications' instead of generic blue */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute top-20 left-10 w-32 h-32 bg-brand/10 rounded-full blur-3xl animate-float"></div>
                <div className="absolute bottom-20 right-10 w-48 h-48 bg-aiApplications/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-brand/5 rounded-full blur-2xl animate-float" style={{ animationDelay: '4s' }}></div>
            </div>

            {/* z-10: Main Content */}
            <div className="container mx-auto px-4 sm:px-6 relative z-10">
                <div className="max-w-5xl mx-auto text-center">

                    {/* Badge */}
                    <motion.div
                        className="inline-flex items-center gap-2 bg-white border border-slate-200 shadow-sm rounded-full px-3 py-1 sm:px-4 sm:py-2 mb-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0 }}
                    >
                        {/* RESTORED: bg-aiApplications */}
                        <div className="w-2 h-2 bg-aiApplications rounded-full animate-pulse"></div>
                        <span className="text-xs sm:text-sm text-slate-600 font-medium">
                            Investing across Europe, Middle East & India
                        </span>
                    </motion.div>

                    {/* Main Heading */}
                    <motion.h1
                        className="text-3xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-4 leading-tight font-serif"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        Backing ambitious founders building{" "}
                        {/* RESTORED: Original gradient from-blue-400 to-cyan-400 */}
                        <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                            AI-native platforms
                        </span>
                    </motion.h1>

                    {/* Subheading */}
                    <motion.p
                        className="text-base sm:text-lg sm:text-xl text-slate-600 mb-6 max-w-3xl mx-auto leading-relaxed font-sans"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        Amama Ventures backs ambitious founders building AI-native, infrastructure, and global operating platforms across Europe, the Middle East, and India. Our integrated capital strategy includes <b className="text-slate-900">non-dilutive financing via Amama Bypass</b>.
                    </motion.p>

                    {/* Location Tags */}
                    <motion.div
                        className="flex flex-wrap justify-center gap-3 mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        {['Tallinn', 'Dubai', 'Mumbai'].map((city) => (
                            <div
                                key={city}
                                className="px-3 py-1 sm:px-4 sm:py-2 bg-white border border-slate-200 rounded-lg text-slate-600 text-xs sm:text-sm font-medium shadow-sm font-sans"
                            >
                                {city}
                            </div>
                        ))}
                    </motion.div>

                    {/* CTA Buttons */}
                    <motion.div
                        className="flex flex-col sm:flex-row gap-4 justify-center mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        <Link to="/portfolio">
                            {/* UPDATED: Uses bg-brand to respect primary color on light theme */}
                            <Button className="w-full sm:w-auto h-10 px-6 text-sm sm:h-11 sm:px-8 sm:text-base bg-gradient-accent hover:opacity-90 text-white border-0">
                                Explore Portfolio
                            </Button>
                        </Link>
                        <Link to="/for-founders">
                            {/* UPDATED: Uses text-brand/border-brand for the outline button */}
                            <Button variant="outline" className="w-full sm:w-auto h-10 px-6 text-sm sm:h-11 sm:px-8 sm:text-base border-slate-300 text-brand hover:bg-brand/5">
                                For Founders
                            </Button>
                        </Link>
                    </motion.div>

                    {/* Scroll Indicator */}
                    <motion.button
                        onClick={handleScroll}
                        className="inline-flex items-center gap-2 text-slate-400 hover:text-brand transition-colors group font-sans"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                    >
                        <span className="text-xs sm:text-sm font-medium">Discover Our Focus</span>
                        <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                    </motion.button>
                </div>
            </div>

        </section>
    );
};

export default Hero;